import { FC, useEffect, useState } from "react";
import { httpRequest } from "@services/http";

/**
 * What the client asked for when they requested this GuidePost (MPN-1622).
 *
 * Sits between the tags and the review comments, which is where the rebrander is already
 * looking. Before this, the whole request shared one note filed as a todo, so whoever
 * rebranded a GuidePost had to find that todo and work out which sentence in it was about
 * the ad in front of them.
 *
 * Renders nothing when the client asked for nothing. The server sends null in that case
 * rather than four empty strings, so an empty heading block cannot appear and read as
 * something failing to load.
 */
export type CustomizationRequestFields = {
  preferred_naming?: string;
  preferred_cta?: string;
  messaging_notes?: string;
  image_video_notes?: string;
};

type Props = { adId?: number | null };

/** Order matches the request modal, so a client and a rebrander read the same list. */
const FIELDS: { key: keyof CustomizationRequestFields; label: string }[] = [
  { key: "preferred_naming", label: "Preferred naming conventions" },
  { key: "preferred_cta", label: "Preferred CTA" },
  { key: "messaging_notes", label: "Additional messaging notes" },
  { key: "image_video_notes", label: "Image/Video" },
];

const CustomizationRequests: FC<Props> = ({ adId }) => {
  const [requests, setRequests] = useState<CustomizationRequestFields | null>(null);

  // Fetched by ad, matching AdTagsReadOnly beside it. The editor opens from the catalog
  // as well as from an engagement and does not always know which engagement it is in.
  useEffect(() => {
    let cancelled = false;
    if (!adId) {
      setRequests(null);
      return;
    }
    (async () => {
      try {
        const url = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/engagements/ads/${adId}/customization-requests/`;
        const data = await httpRequest<CustomizationRequestFields | null>(url, { method: "GET" });
        if (!cancelled) setRequests(data ?? null);
      } catch {
        // A GuidePost that was never requested through the store has none, and the
        // panel simply does not appear. Nothing here is worth an error message.
        if (!cancelled) setRequests(null);
      }
    })();
    return () => { cancelled = true; };
  }, [adId]);

  if (!requests) return null;

  // A field the client left blank is left out rather than shown as an empty heading:
  // four labels with nothing under them look like a load that failed, not like a client
  // who only cared about the CTA.
  const filled = FIELDS.filter(({ key }) => (requests[key] ?? "").trim().length > 0);
  if (filled.length === 0) return null;

  return (
    <div className="ceh-customization-requests">
      <div className="ceh-review-comment-list-header">Customization Requests</div>
      {filled.map(({ key, label }) => (
        <div key={key} className="ceh-customization-request">
          <div className="ceh-customization-request__label">{label}</div>
          <div className="ceh-customization-request__value">{requests[key]}</div>
        </div>
      ))}
    </div>
  );
};

export default CustomizationRequests;
